import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded'
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded'
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import API from '../../../../../services/API'
import { GlobalContext } from '../../../../../App'
import DetailsBox from '../../../../../components/Cards/DetailsBox'
import PlanningGapsList from './PlanningGapsList'

const SEVERITY_COLOR = { low: 'success', medium: 'warning', high: 'error' }

const confidenceColor = (score) => {
  if (score >= 80) return 'success'
  if (score >= 50) return 'warning'
  return 'error'
}

const RISK_LEVEL_LABEL = {
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
  very_high: 'Very High',
}

const riskLevelColor = (level) => {
  if (level === 'low') return 'success'
  if (level === 'moderate') return 'warning'
  return 'error'
}

const rangeLabel = (min, max) =>
  min == null || max == null ? undefined : `${min}–${max}`

const severityTone = (theme, severity) => {
  const colorName = SEVERITY_COLOR[severity] ?? 'info'
  const paletteColor = theme.palette[colorName]?.main || theme.palette.grey[500]
  return {
    color: paletteColor,
    bg: alpha(paletteColor, 0.08),
    border: alpha(paletteColor, 0.18),
  }
}

const SummaryPill = ({ icon: Icon, label, value, color = 'anBlue' }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1}
    sx={(theme) => {
      const paletteColor = theme.palette[color]?.main || color
      return {
        minWidth: 0,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: alpha(paletteColor, 0.08),
        border: `1px solid ${alpha(paletteColor, 0.16)}`,
      }
    }}
  >
    <Box
      sx={(theme) => {
        const paletteColor = theme.palette[color]?.main || color
        return {
          width: 34,
          height: 34,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(paletteColor, 0.12),
          color: paletteColor,
          flex: '0 0 auto',
        }
      }}
    >
      <Icon fontSize="small" />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 800 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 900 }}
        noWrap
        title={String(value)}
      >
        {value}
      </Typography>
    </Box>
  </Stack>
)

const MetricTile = ({
  icon: Icon,
  label,
  value,
  detail,
  color = 'anBlue',
  prominent = false,
}) => (
  <Box
    sx={(theme) => {
      const paletteColor = theme.palette[color]?.main || color
      return {
        // minHeight: prominent ? 156 : 132,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(paletteColor, prominent ? 0.24 : 0.14)}`,
        bgcolor: prominent
          ? alpha(paletteColor, 0.075)
          : alpha(paletteColor, 0.035),
        boxShadow: prominent
          ? `0 14px 34px ${alpha(paletteColor, 0.1)}`
          : 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 1.5,
      }
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0 }}
      >
        {label}
      </Typography>
      <Box
        sx={(theme) => {
          const paletteColor = theme.palette[color]?.main || color
          return {
            width: 34,
            height: 34,
            borderRadius: 1.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(paletteColor, 0.11),
            color: paletteColor,
            flex: '0 0 auto',
          }
        }}
      >
        <Icon fontSize="small" />
      </Box>
    </Stack>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant={prominent ? 'h3' : 'h4'}
        sx={(theme) => {
          const paletteColor = theme.palette[color]?.main || color
          return {
            color: paletteColor,
            fontWeight: 950,
            lineHeight: 1.05,
            letterSpacing: 0,
            overflowWrap: 'anywhere',
          }
        }}
      >
        {value ?? '—'}
      </Typography>
      {detail && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontWeight: 700 }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  </Box>
)

const RiskDriverRow = ({ driver }) => (
  <Box
    sx={(theme) => {
      const tone = severityTone(theme, driver.severity)
      return {
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${tone.border}`,
        bgcolor: tone.bg,
      }
    }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      gap={1.5}
      alignItems="flex-start"
    >
      <Box
        sx={(theme) => {
          const tone = severityTone(theme, driver.severity)
          return {
            width: 38,
            height: 38,
            borderRadius: 1.5,
            display: 'grid',
            placeItems: 'center',
            color: tone.color,
            bgcolor: alpha(tone.color, 0.1),
            flex: '0 0 auto',
          }
        }}
      >
        <WarningAmberRoundedIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" fontWeight={900}>
            {driver.label}
          </Typography>
          <Chip
            size="small"
            label={driver.severity}
            color={SEVERITY_COLOR[driver.severity] ?? 'default'}
            sx={{ height: 22, textTransform: 'capitalize', fontWeight: 800 }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {driver.explanation}
        </Typography>
      </Box>
    </Stack>
  </Box>
)

// Server-calculated planning support data — deliberately kept isolated from
// the event planner's tracked-dirty-state/diff-save machinery, since nothing
// here is directly user-edited free text.
const PlanningIntelligence = ({ eventID, onNavigateToTab }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [recalculating, setRecalculating] = useState(false)
  const { setToast } = useContext(GlobalContext)

  const loadIntelligence = () => {
    setLoading(true)
    API.get(`/events/${eventID}/planning-intelligence`)
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
        } else {
          setToast({
            message: 'Unable to load planning intelligence',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to load planning intelligence',
          severity: 'error',
        }),
      )
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (eventID) loadIntelligence()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventID])

  const handleRecalculate = () => {
    setRecalculating(true)
    API.post(`/events/${eventID}/recalculate-planning`)
      .then((res) => {
        if (res.status === 200) {
          setData((prev) => ({ ...prev, ...res.data, persisted: true }))
        } else {
          setToast({
            message: 'Unable to recalculate planning intelligence',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to recalculate planning intelligence',
          severity: 'error',
        }),
      )
      .finally(() => setRecalculating(false))
  }

  if (loading || !data) return <LinearProgress />

  const { demandPrediction } = data
  const lastCalculatedLabel = data.audit?.calculatedAt
    ? moment(data.audit.calculatedAt).format('DD/MM/YYYY HH:mm')
    : 'Live estimate'
  const peakDemandLabel = demandPrediction.peakHourStart
    ? `${demandPrediction.peakHourStart}–${demandPrediction.peakHourEnd}`
    : '—'
  const clinicalRiskLabel = RISK_LEVEL_LABEL[data.clinicalRiskLevel] ?? '—'

  return (
    <Stack gap={3}>
      <DetailsBox
        title="Planning Intelligence"
        caption="Live planning support based on event details, previous data and known risk factors. These figures are planning estimates and should be reviewed by a competent event medical planner."
        buttonRight={
          <Button
            variant="contained"
            color="anBlue"
            startIcon={<RefreshRoundedIcon />}
            onClick={handleRecalculate}
            disabled={recalculating}
            sx={{ boxShadow: 'none' }}
          >
            {recalculating ? 'Recalculating...' : 'Recalculate'}
          </Button>
        }
        sx={{ overflow: 'hidden' }}
      >
        <Stack gap={2.5}>
          <Box
            sx={(theme) => ({
              p: { xs: 1.5, md: 2 },
              borderRadius: 2,
              bgcolor: alpha(theme.palette.anBlue.main, 0.045),
              border: `1px solid ${alpha(theme.palette.anBlue.main, 0.12)}`,
            })}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <SummaryPill
                  icon={FactCheckRoundedIcon}
                  label="Confidence"
                  value={`${data.confidenceScore}%`}
                  color={confidenceColor(data.confidenceScore)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SummaryPill
                  icon={WarningAmberRoundedIcon}
                  label="Clinical risk"
                  value={clinicalRiskLabel}
                  color={riskLevelColor(data.clinicalRiskLevel)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SummaryPill
                  icon={AccessTimeRoundedIcon}
                  label={
                    data.audit?.calculatedAt ? 'Last calculated' : 'Status'
                  }
                  value={lastCalculatedLabel}
                />
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MetricTile
                prominent
                icon={LocalHospitalRoundedIcon}
                label="Expected presentations"
                value={demandPrediction.expectedPresentationsPlanning ?? '—'}
                detail={
                  rangeLabel(
                    demandPrediction.expectedPresentationsMin,
                    demandPrediction.expectedPresentationsMax,
                  )
                    ? `Planning range ${rangeLabel(
                        demandPrediction.expectedPresentationsMin,
                        demandPrediction.expectedPresentationsMax,
                      )}`
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MetricTile
                prominent
                icon={TrendingUpRoundedIcon}
                label="Hospital transfers"
                value={demandPrediction.hospitalTransfersPlanning ?? '—'}
                detail={
                  rangeLabel(
                    demandPrediction.hospitalTransfersMin,
                    demandPrediction.hospitalTransfersMax,
                  )
                    ? `Planning range ${rangeLabel(
                        demandPrediction.hospitalTransfersMin,
                        demandPrediction.hospitalTransfersMax,
                      )}`
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricTile
                icon={SpeedRoundedIcon}
                label="Presentation rate"
                value={demandPrediction.pprPlanning ?? '—'}
                detail={
                  demandPrediction.pprPlanning != null
                    ? 'Per 1,000 attendees'
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricTile
                icon={TimelineRoundedIcon}
                label="Peak demand"
                value={peakDemandLabel}
                detail="Highest expected treatment pressure"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MetricTile
                icon={AutoAwesomeRoundedIcon}
                label="Referral estimate"
                value={demandPrediction.referralEstimate ?? '—'}
                detail="Likely onward referrals"
              />
            </Grid>
          </Grid>
        </Stack>
      </DetailsBox>

      <DetailsBox title="Risk drivers">
        {data.riskDrivers.length === 0 ? (
          <Box
            sx={(theme) => ({
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.07),
              border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
            })}
          >
            <Stack direction="row" gap={1.5} alignItems="center">
              <FactCheckRoundedIcon color="success" />
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={700}
              >
                No notable risk drivers identified from the information
                currently entered.
              </Typography>
            </Stack>
          </Box>
        ) : (
          <Stack gap={1.25}>
            {data.riskDrivers.map((driver) => (
              <RiskDriverRow key={driver.key} driver={driver} />
            ))}
          </Stack>
        )}
      </DetailsBox>

      <PlanningGapsList
        gaps={data.planningGaps}
        onNavigateToTab={onNavigateToTab}
      />
    </Stack>
  )
}

export default PlanningIntelligence
